import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  QrCode, ScanLine, CheckCircle2, Users, Clock, 
  Loader2, Search, UserCheck, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientCheckin = () => {
  const { user, permissions } = useAuth();
  const [attendees, setAttendees] = useState([]);
  const [checkinStats, setCheckinStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [myQRCode, setMyQRCode] = useState(null);

  // Check if user has registration permission
  const canManageCheckin = permissions?.includes('manage_attendance');

  useEffect(() => {
    if (user?.event_id) {
      fetchData();
      if (!canManageCheckin) {
        fetchMyQRCode();
      }
    }
  }, [user, permissions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/event/checkin/status?event_id=${user.event_id}`)
      ];
      
      if (canManageCheckin) {
        requests.push(axios.get(`${API_URL}/api/event/attendees`));
      }
      
      const responses = await Promise.all(requests);
      setCheckinStats(responses[0].data);
      
      if (canManageCheckin && responses[1]) {
        setAttendees(responses[1].data);
      }
    } catch (error) {
      console.error('Failed to load check-in data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyQRCode = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/checkin/generate-qr/${user.id}`);
      setMyQRCode(response.data);
    } catch (error) {
      console.error('Failed to generate QR code');
    }
  };

  const handleGenerateQR = async (attendeeId) => {
    try {
      const response = await axios.get(`${API_URL}/api/event/checkin/generate-qr/${attendeeId}`);
      setQrData(response.data);
      setSelectedAttendee(attendees.find(a => a.id === attendeeId));
      setQrDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleVerifyCode = async (code = null) => {
    const qrCode = code || manualCode;
    if (!qrCode) {
      toast.error('Please enter a check-in code');
      return;
    }

    setVerifying(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/event/checkin/verify?qr_data=${encodeURIComponent(qrCode)}&event_id=${user.event_id}`
      );
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLastCheckin(response.data);
        fetchData();
      } else if (response.data.status === 'already_checked_in') {
        toast.warning(response.data.message);
        setLastCheckin(response.data);
      }
      
      setManualCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid check-in code');
    } finally {
      setVerifying(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const filteredAttendees = attendees.filter(a =>
    a.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </ClientLayout>
    );
  }

  // Regular attendee view - show their own QR code
  if (!canManageCheckin) {
    return (
      <ClientLayout>
        <div className="space-y-6 animate-fade-in" data-testid="client-checkin-page">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-maroon">My Check-in QR Code</h1>
            <p className="text-gray-600 mt-2">Show this QR code at the registration desk</p>
          </div>

          <Card className="border-0 shadow-lg bg-white max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              {myQRCode ? (
                <>
                  <div className="bg-white p-6 rounded-xl inline-block shadow-inner border">
                    <QRCode value={myQRCode.qr_data} size={200} />
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Code: <span className="font-mono font-bold text-maroon">{myQRCode.checkin_code}</span>
                  </p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  {user?.batch_year && (
                    <Badge className="mt-2 bg-gold text-gray-900">Batch {user.batch_year}</Badge>
                  )}
                </>
              ) : (
                <div className="py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-maroon mx-auto" />
                  <p className="mt-4 text-gray-500">Generating your QR code...</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Button 
            variant="outline" 
            onClick={fetchMyQRCode}
            className="mx-auto flex gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh QR Code
          </Button>
        </div>
      </ClientLayout>
    );
  }

  // Registration committee view - full check-in station
  return (
    <ClientLayout>
      <div className="space-y-6 animate-fade-in" data-testid="client-checkin-station">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <ScanLine className="h-8 w-8" />
              Check-in Station
            </h1>
            <p className="text-gray-600 mt-1">Scan QR codes or search attendees</p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="gap-2"
            data-testid="refresh-checkin-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Committee Badge */}
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 flex items-center gap-3">
          <Badge className="bg-gold text-gray-900">Registration Committee</Badge>
          <span className="text-sm text-gray-700">You have permission to manage check-ins</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{checkinStats?.total_attendees || 0}</p>
              <p className="text-sm text-gray-500">Total Attendees</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <p className="text-3xl font-bold text-green-700">{checkinStats?.checked_in || 0}</p>
              <p className="text-sm text-green-600">Checked In</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto text-amber-500 mb-2" />
              <p className="text-3xl font-bold text-amber-700">{checkinStats?.not_checked_in || 0}</p>
              <p className="text-sm text-amber-600">Not Yet</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-maroon text-white">
            <CardContent className="p-4 text-center">
              <p className="text-4xl font-bold">{checkinStats?.percentage || 0}%</p>
              <p className="text-sm text-white/80">Attendance Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Manual Code Entry */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-maroon to-maroon-hover text-white">
          <CardContent className="p-6">
            <h3 className="font-heading text-xl mb-4 flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Manual Check-in
            </h3>
            <div className="flex gap-3">
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Enter check-in code (e.g., BTRN-XXXXXXXX)"
                className="flex-1 bg-white/20 border-white/30 text-white placeholder:text-white/50"
                data-testid="manual-code-input"
              />
              <Button
                onClick={() => handleVerifyCode()}
                disabled={verifying}
                className="bg-white text-maroon hover:bg-white/90"
                data-testid="verify-code-btn"
              >
                {verifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <ScanLine className="h-4 w-4 mr-2" />
                    Verify
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Last Check-in Result */}
        {lastCheckin && (
          <Card className={`border-0 shadow-sm ${
            lastCheckin.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                {lastCheckin.status === 'success' ? (
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                ) : (
                  <Clock className="h-12 w-12 text-amber-500" />
                )}
                <div>
                  <p className="text-lg font-bold text-gray-900">{lastCheckin.message}</p>
                  <p className="text-sm text-gray-500">
                    {lastCheckin.attendee?.first_name} {lastCheckin.attendee?.last_name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendee List */}
        <Card className="border-0 shadow-sm bg-white">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="font-heading text-xl text-maroon">Attendee List</CardTitle>
                <CardDescription>Generate QR codes and manage check-ins</CardDescription>
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search attendees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="attendee-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredAttendees.map((attendee) => (
                <div 
                  key={attendee.id}
                  className={`flex items-center justify-between p-4 rounded-xl border ${
                    attendee.checked_in 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                  data-testid={`attendee-row-${attendee.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 border-2 border-white shadow">
                      <AvatarImage src={attendee.profile_photo} />
                      <AvatarFallback className={attendee.checked_in ? 'bg-green-500 text-white' : 'bg-maroon text-white'}>
                        {getInitials(attendee.first_name, attendee.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {attendee.first_name} {attendee.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{attendee.email}</p>
                      {attendee.batch_year && (
                        <Badge variant="outline" className="text-xs mt-1">
                          Batch {attendee.batch_year}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {attendee.checked_in ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Checked In
                      </Badge>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateQR(attendee.id)}
                          data-testid={`generate-qr-${attendee.id}`}
                        >
                          <QrCode className="h-4 w-4 mr-1" />
                          QR Code
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleGenerateQR(attendee.id)}
                          data-testid={`quick-checkin-${attendee.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Check In
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-md text-center">
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">QR Check-in Code</DialogTitle>
              <DialogDescription>
                {selectedAttendee?.first_name} {selectedAttendee?.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="p-6">
              {qrData && (
                <div className="bg-white p-4 rounded-xl inline-block shadow-lg">
                  <QRCode value={qrData.qr_data} size={200} />
                </div>
              )}
              <p className="mt-4 text-sm text-gray-500">
                Code: <span className="font-mono font-bold">{qrData?.checkin_code}</span>
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={() => {
                  handleVerifyCode(qrData?.qr_data);
                  setQrDialogOpen(false);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as Checked In
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ClientCheckin;
